import type { DiffNavigationHandlers } from '@/lib/stores/navigation/diff-navigation-store';
import type { CodeContent } from '@/types/github';

import { findSymbolDefinition } from './find-symbol-definition';

import { getCandidateDiffFileElementIds } from '@/lib/stores/navigation/diff-file-element-ids';
import { highlightLine } from '@/lib/utils/line-highlighting';

const HIGHLIGHT_DELAY_MS = 200;
const LINE_SCROLL_RETRY_MAX = 15;
const LINE_SCROLL_RETRY_DELAY_MS = 80;
const NAVBAR_OFFSET = 72;

let popstateListenerInstalled = false;

// Lazily installs a global popstate handler so the browser back button scrolls
// back to the group header (or other origin element) the user navigated from.
function ensurePopstateListener(): void {
  if (popstateListenerInstalled) {
    return;
  }
  popstateListenerInstalled = true;

  window.addEventListener('popstate', (event) => {
    const originId = (event.state as { symbolNavOrigin?: string } | null)?.symbolNavOrigin;
    if (originId) {
      const el = document.getElementById(originId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });
}

function pushOriginState(originElementId: string): void {
  ensurePopstateListener();
  // Stamp the current entry so it's available when the user navigates back
  window.history.replaceState({ ...window.history.state, symbolNavOrigin: originElementId }, '');
  // Push a clean entry for the destination (no hash change = no hashchange event)
  window.history.pushState(null, '');
}

function resolveFileElementId(filename: string): string | null {
  for (const candidateId of getCandidateDiffFileElementIds(filename)) {
    if (document.getElementById(candidateId)) {
      return candidateId;
    }
  }
  return null;
}

function scrollToLineInFile(
  fileId: string,
  lineNumber: number,
  side: 'new' | 'old',
  attempt = 0,
): void {
  const fileElement = document.getElementById(fileId);
  if (!fileElement) {
    return;
  }

  const sideSelector =
    side === 'new'
      ? '[data-side="new"], [data-additions], [data-line-type="change-addition"]'
      : '[data-side="old"], [data-deletions], [data-line-type="change-deletion"]';

  const candidates = [
    ...Array.from(
      fileElement.querySelectorAll(
        `[data-line="${lineNumber}"], [data-alt-line="${lineNumber}"], [data-line-number="${lineNumber}"]`,
      ),
    ),
  ].filter((el): el is HTMLElement => el instanceof HTMLElement);

  // Prefer a line matching the requested side
  const sidedCandidate = candidates.find((el) => el.closest(sideSelector));
  const target = sidedCandidate ?? candidates[0];

  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  // @pierre/diffs renders inside shadow roots
  const diffContainers = fileElement.querySelectorAll('diffs-container');
  for (const container of diffContainers) {
    if (!(container instanceof HTMLElement) || !container.shadowRoot) {
      continue;
    }

    const shadowCandidates = Array.from(
      container.shadowRoot.querySelectorAll(
        `[data-line="${lineNumber}"], [data-alt-line="${lineNumber}"], [data-line-number="${lineNumber}"]`,
      ),
    ).filter((el): el is HTMLElement => el instanceof HTMLElement);

    const sidedShadowCandidate = shadowCandidates.find((el) => el.closest(sideSelector));
    const shadowTarget = sidedShadowCandidate ?? shadowCandidates[0];
    if (shadowTarget) {
      shadowTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
  }

  // Retry if the diff rows haven't rendered yet
  if (attempt < LINE_SCROLL_RETRY_MAX) {
    window.setTimeout(
      () => scrollToLineInFile(fileId, lineNumber, side, attempt + 1),
      LINE_SCROLL_RETRY_DELAY_MS,
    );
    return;
  }

  // Last resort: scroll to the file header
  fileElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.scrollBy(0, -NAVBAR_OFFSET);
}

const FILE_RESOLVE_RETRY_MAX = 10;
const FILE_RESOLVE_RETRY_DELAY_MS = 100;

// Retries resolving the file's DOM element before scrolling + highlighting.
// The file section may not be in the DOM yet when navigateToFile triggers an
// expand, so a single timeout is insufficient.
function resolveFileAndScrollToLine(
  filename: string,
  lineNumber: number,
  side: 'new' | 'old',
  attempt = 0,
): void {
  const fileId = resolveFileElementId(filename);
  if (fileId) {
    scrollToLineInFile(fileId, lineNumber, side);
    highlightLine(fileId, lineNumber, side);
    return;
  }

  if (attempt < FILE_RESOLVE_RETRY_MAX) {
    window.setTimeout(
      () => resolveFileAndScrollToLine(filename, lineNumber, side, attempt + 1),
      FILE_RESOLVE_RETRY_DELAY_MS,
    );
  }
}

/**
 * Resolves a symbol name to its definition in the PR diff and navigates to it.
 * Returns true if a definition was found and navigation was triggered.
 */
export function navigateToSymbolDefinition(
  symbolName: string,
  allFiles: CodeContent[],
  navigateToFile: DiffNavigationHandlers['navigateToFile'],
  preferredFiles?: CodeContent[],
  originElementId?: string,
): boolean {
  const definition = findSymbolDefinition(symbolName, allFiles, preferredFiles);
  if (!definition) {
    return false;
  }

  if (originElementId) {
    pushOriginState(originElementId);
  }

  navigateToFile(definition.filename, { expandFile: true, source: 'programmatic' });

  window.setTimeout(() => {
    resolveFileAndScrollToLine(definition.filename, definition.lineNumber, definition.side);
  }, HIGHLIGHT_DELAY_MS);

  return true;
}

/**
 * Navigates directly to a known file + line without needing to search.
 * Used when the AI has provided exact symbol locations.
 */
export function navigateToFileAndLine(
  filename: string,
  lineNumber: number,
  navigateToFile: DiffNavigationHandlers['navigateToFile'],
  side: 'new' | 'old' = 'new',
  originElementId?: string,
): void {
  if (originElementId) {
    pushOriginState(originElementId);
  }

  navigateToFile(filename, { expandFile: true, source: 'programmatic' });

  window.setTimeout(() => {
    resolveFileAndScrollToLine(filename, lineNumber, side);
  }, HIGHLIGHT_DELAY_MS);
}

export { findSymbolDefinition };
